import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DynamicForm from './DynamicForm';
import { Col, FormGroup, Input, Label } from 'reactstrap';
import { Tooltip } from 'react-tooltip';
import DateTime from 'react-datetime';
import 'assets/css/componentStyles/DynamicInput.css'
import 'react-datetime/css/react-datetime.css';
import {handleDateInput, formatDateForBackend} from "utils/formatting/dateUtils"
import MediaViewer from './MediaViewer';
import GenericDropdown from 'components/dropdowns/GenericDropdown';
import IconButtonWithTooltip from 'components/buttons/IconButtonWithTooltip';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import Speakable from './Speakable';
import OpenDetailsButton from 'components/buttons/OpenDetailsButton';
import ObjectSelector from '../objectSelector/ObjectSelector';
import ImageUrlInput from './ImageUrlInput';
import BadgeSelector from './BadgeSelector';
import EmojiPicker from 'emoji-picker-react';
import EmoticonPicker from './EmoticonPicker';
import UserSelect from 'components/dropdowns/UserSelect';
import SendDaysSelector from '../modules/schedulers/SendDaysSelector';
import SkipDaysInput from '../modules/schedulers/SkipDaysInput';
import BreaksInput from '../modules/schedulers/BreaksInput';
import JsonViewer from './JsonViewer';

const DynamicInput = ({ label, value, type="text", options, onChange, minRange="0", maxRange="1", rangeStep="0.01", readOnly=false, height, color, icon, showText=true, linkValue }) => {
    
    const { t } = useTranslation();
    const inputId = label
    const [isValidDate, setIsValidDate] = useState(true);
    const uniqueId = useId();
    const inputRef = useRef(null);

    //const dateCET = DateTime.fromISO(serverTimestamp, { zone: "Europe/Berlin" });
    //const localDate = dateCET.toLocal();

    const handleChange = (e) => {
        switch(type){
            case 'checkbox':
                return onChange({target: {value: e.target.checked, id: e.id}})
            case 'iconSelect':
                return onChange({target: {value: e.value || e.label}})
            case 'elementSelect':
                console.log('select: ', e.value)
                return onChange({target: {value: e.value}})
            case 'editor': 
            case 'multiSelect':
            case 'emojiPicker':
            case 'link':
                return onChange({target: {value: e}})
            default: 
                return onChange(e)//.target.value)
        }
    }

    useEffect(() => {
        if ((type === 'textarea') &&  inputRef.current) {
          inputRef.current.style.height = "auto"; // Reset height
          inputRef.current.style.height = inputRef.current.scrollHeight + "px"; // Adjust height
        }
      }, [value]);

    const commonInputProps = {
        id: inputId,
        value: value ?? '',
        onChange: handleChange,
        className: "form-control",
        type: type,
        style: 
            (type === 'datetime' || type === 'date' || type === 'datetime-local' || type === 'time') ? 
                {marginTop:'4px', height:"38px", maxWidth:"13em"} 
            : 
                {marginTop:'0px', height:"38px"},
        readOnly: readOnly
    };

    const getBackgroundSize = (value) => {
        return { backgroundSize: `${(value*100) / maxRange}% 100%` };
    };

    const handleChangeDateTime = (e) => {
        try{
            onChange({target: {value: formatDateForBackend(e)}})
            setIsValidDate(true)
        } catch(e){
            setIsValidDate(false)
            const timer = setTimeout(() => {
                setIsValidDate(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }

    const handleFocus = () => {
        console.log('handle Focus', value)
        if (value === null) {
            handleChangeDateTime(new Date()); // Set to current date and time on first interaction
        }
    };

    let element = null;
    switch (type) {
        case 'image':
            element = <ImageUrlInput value={value} label={label} onChange={onChange} readOnly={readOnly}/>
            //element = <ImageUpload value={value} onChange={onChange}/>
            break;
        case 'array':
            element = <DynamicForm value={value} onChange={onChange} />;
            break;
        case 'select':
            element = (
                <Input {...commonInputProps} type="select">
                    {options?.map((option, index) => (
                        <option key={index} value={option?.value ?? option}>
                            {option?.label ? option.label : t(option)}
                        </option>
                    ))}
                </Input>
            );
            break;
        case 'iconSelect':
            element = (
                <GenericDropdown 
                    style={{maxWidth:'6em'}} 
                    items={options} 
                    onItemSelect={handleChange} caret={false}
                    label={label}
                    readOnly={readOnly}
                >
                    {showText &&
                        <div style={{fontSize: '10px', whiteSpace: 'nowrap', overflowX: 'hidden', overflowY: 'hidden', marginTop:'2px'}}>
                            {t(options.find(option => option.value === value)?.label)}
                        </div> 
                    }
                    <IconButtonWithTooltip 
                        icon={options.find(option => option.value === value)?.icon || faCircleQuestion} 
                        plainText={t(options.find(option => option.value === value)?.label)} 
                        type="IconOnly"
                        size="2xl"
                        color={color}
                    />
                </GenericDropdown>
            );
            break;
        case 'elementSelect':
            element = (
                <GenericDropdown 
                    style={{maxWidth:'6em'}} 
                    items={options} 
                    onItemSelect={handleChange} caret={false}
                    label={label}
                >
                    {options.find(option => option.value === value)?.element || 
                        <IconButtonWithTooltip 
                            icon={faCircleQuestion}  
                            type="IconOnly"
                            size="2xl"
                        />}
                </GenericDropdown>
            );
            break;
        case 'userSelect':
            element = (
                <UserSelect
                    label={label} 
                    value={value} 
                    onChange={onChange}
                    readOnly={readOnly}/>
            );
            break;
        case 'textarea':
            element = (
                <Input
                    {...commonInputProps}
                    placeholder={label ? t(`${label}`) : ''}
                    style={{
                        resize: value?.length > 30 ? 'vertical' : 'none',
                        maxHeight: height || (value?.length > 30 ? '400px' : '38px'),
                        marginTop: value?.length > 30 ? '4px' : '4px',
                        overflow: 'hidden',
                    }}
                    innerRef={inputRef}
                    onInput={(e) => {
                        e.target.style.height = "auto"; // Reset height
                        e.target.style.height = e.target.scrollHeight + "px"; // Adjust height dynamically
                      }}
                />
            );
            break;
        case 'json':
            element = <Input
                    {...commonInputProps}
                    type="textarea"
                    rows={value ? 5 : 1}
                    placeholder={t(`${label}`)}
                    className="font-monospace"
                    style={{
                        resize: 'vertical',
                        marginTop: value?.length > 30 ? '4px' : '4px', fontSize: '0.85rem', whiteSpace: 'pre-wrap'
                    }}
                    value={typeof value === 'object'
                        ? value ? JSON.stringify(value, null, 2) : ''
                        : value || ''}
                    innerRef={inputRef}
                    />
            break
        case 'jsonViewer':
            element = <JsonViewer data={value} onChange={onChange}/>
            break
        case 'checkbox':
            // element = (<Input disabled={readOnly} {...commonInputProps} checked={!!value && value != 'false'}/>)
            element = (
                <>
                    <Input disabled={readOnly} {...commonInputProps} checked={!!value && value != 'false'}/>
                    <Label for={uniqueId} className="text-center w-100">{label}</Label>
                </>
            );
            break;
        case 'email':
        case 'password':
                element = (
                    <Input {...commonInputProps} placeholder={t(` ${label}`)} 
                    style={{
                        maxHeight: '38px',
                        marginTop: '4px',
                    }}/>
                );
                break;
        case 'date':
                element = (
                    <Input {...commonInputProps} value={handleDateInput(type, value) || undefined}/>
                );
                break;        
        case 'datetime':
            //moment(new Date())
            element = (
                <DateTime {...commonInputProps} value={value} 
                    timeFormat="HH:mm"
                    onChange={handleChangeDateTime}
                    onOpen={(e) =>{handleFocus(e);console.log(e)}}
                    inputProps={{readOnly: readOnly, className: `form-control ${!isValidDate ? 'invalid-date' : ''}`}}
                />
            );
            break;
        case 'datetime-local': 
            element = (
                <Input {...commonInputProps} value={handleDateInput(type, value)} />
            );
            break;
        case 'time':
            element = (
                <Input {...commonInputProps} value={value} />
            );
            break;
        case 'sendDays':
            element = <SendDaysSelector value={value} onChange={onChange} readOnly={readOnly} />;
            break;
        case 'skipDays':
            element = <SkipDaysInput value={value} onChange={onChange} readOnly={readOnly} />;
            break;
        case 'breaks':
            element = <BreaksInput value={value} onChange={onChange} readOnly={readOnly} />;
            break;
        case 'multiSelect':
                element = (
                    <Col className='mb-3'><BadgeSelector obj={options} assignedBadges={value} saveBadges={handleChange} addBadges={false} showSuggestions={true} /></Col>
                );
                break;
        case 'range':
            element = (
                <>
                    <Input  
                        {...commonInputProps}
                        min={minRange}
                        max={maxRange}
                        step={rangeStep}
                        style={getBackgroundSize(value)}
                        data-tooltip-id={uniqueId}
                        data-tooltip-content={value}
                    />
                    <Tooltip id={uniqueId} style={{zIndex: 9999}}/>
                </>
            );
            break;
        case 'editor':
                element = 
                    <Speakable 
                        value={value} 
                        onChange={handleChange} 
                        readOnly={readOnly}/>
                        //<RichTextEditor initialContent={value} onContentChange={handleChange} readOnly={readOnly}/>
            break;
        case 'pdf':
            element = <MediaViewer unique_filename={value}/>
            break;
        case 'media':
            element = <MediaViewer uploadId={value}/>
            break;
        case 'link':
            element = <ObjectSelector value={value} onChange={handleChange} obj={options} readOnly={readOnly} label={label} icon={icon} linkValue={linkValue} />//<OpenDetailsButton id={value} obj={options}/>
            break;
        case 'emojiPicker':
            element = <EmoticonPicker value={value} onEmojiClick={handleChange} />
            break
        default:
            element = (<Input
                {...commonInputProps}
                placeholder={label ? t(` ${label}`) : ''} 
                type="textarea"
                style={{
                    resize: value?.length > 30 ? 'vertical' : 'none',
                    maxHeight: value?.length > 30 ? '400px' : '38px',
                    marginTop: value?.length > 30 ? '4px' : '4px',
                    marginBottom: value?.length > 30 ? '8px' : '8px',
                    overflow: 'hidden',
                }}
            />);
    }

    return element;
};

export default DynamicInput;
